import {
  DocumentData,
  Query,
  QueryConstraint,
  Timestamp,
  collection,
  doc,
  query,
  serverTimestamp,
  setDoc,
  where,
} from "firebase/firestore";
import { auth, db, functionsUrl } from "../firebase/firebase";
import { collectionData, docData } from "rxfire/firestore";
import { Observable, map, of } from "rxjs";
import DataTransformationService from "./data-transformation.service";
import { Store } from "../models/store";
import axios from "axios";

function generateId() {
  return doc(collection(db, "stores")).id;
}

function getOne(id: string): Observable<Store> {
  if (!id) return of(undefined);
  const docRef = doc(db, `stores/${id}`);
  return docData(docRef).pipe(
    map((firestoreDocument) =>
      DataTransformationService.convertTimestampsToIsoString(firestoreDocument)
    )
  );
}

function getAllByBrand(tenantId: string, brandId: string): Observable<Store[]> {
  const collectionRef = collection(db, "stores");

  const queryConstraint: QueryConstraint[] = [
    where("tenantId", "==", tenantId),
    where("brandId", "==", brandId),
    where("deleted", "==", false),
  ];

  const collectionQuery: Query<DocumentData> = query(
    collectionRef,
    ...queryConstraint
  );

  return collectionData(collectionQuery).pipe(
    map((collection) => {
      //sort by order
      collection.sort((a, b) => a.storeName.localeCompare(b.storeName));
      //convert timestamps to Date
      collection = collection.map((firestoreDocument) =>
        DataTransformationService.convertTimestampsToIsoString(
          firestoreDocument
        )
      );
      return collection as Store[];
    })
  );
}

function getAllByIds(
  tenantId: string,
  brandId: string,
  storeIds: string[]
): Observable<Store[]> {
  //Only query with valid storeIds array
  if (!storeIds || !Array.isArray(storeIds) || storeIds.length === 0)
    return of([]);

  //Limit query due to firebase limitation
  if (storeIds.length > 10) throw new Error("Currently limited to ten stores.");

  const collectionRef = collection(db, "stores");

  const queryConstraint: QueryConstraint[] = [
    where("tenantId", "==", tenantId),
    where("brandId", "==", brandId),
    where("id", "in", storeIds),
    where("deleted", "==", false),
  ];

  const collectionQuery: Query<DocumentData> = query(
    collectionRef,
    ...queryConstraint
  );

  return collectionData(collectionQuery).pipe(
    map((collection) => {
      //sort by order
      collection.sort(
        (a, b) =>
          (a.createdDate && a.createdDate instanceof Timestamp
            ? a.createdDate.seconds
            : 99999999999) -
          (b.createdDate && b.createdDate instanceof Timestamp
            ? b.createdDate.seconds
            : 99999999999)
      );
      //convert timestamps to Date
      collection = collection.map((firestoreDocument) =>
        DataTransformationService.convertTimestampsToIsoString(
          firestoreDocument
        )
      );
      return collection as Store[];
    })
  );
}

async function saveOne(store: Partial<Store>, isNew: boolean): Promise<any> {
  try {
    if (isNew) {
      store.createdDate = serverTimestamp();
      store.createdByUserAccountId = auth.currentUser.uid;
    } else {
      store.updatedDate = serverTimestamp();
      store.updatedByUserAccountId = auth.currentUser.uid;
    }

    if (store.deleted == undefined) store.deleted = false;

    store = DataTransformationService.convertDatesToFirestoreTimestamp(store);

    let docRef = doc(db, `stores/${store.id}`);
    await setDoc(docRef, store, { merge: true });
    return;
  } catch (err) {
    throw err;
  }
}

async function deleteOne(storeId: string): Promise<any> {
  try {
    let docRef = doc(db, `stores/${storeId}`);
    const documentToDelete = {
      deleted: true,
      deletedDate: serverTimestamp(),
      updatedDate: serverTimestamp(),
      updatedByUserAccountId: auth.currentUser.uid,
    };
    await setDoc(docRef, documentToDelete, { merge: true });
    return;
  } catch (err) {
    throw err;
  }
}

// Uplaods file and returns the file url
async function uploadStoreFile(
  brandId: string,
  storeId: string,
  file: File
): Promise<string> {
  try {
    //configure axios client with bearer token
    const token = await auth.currentUser.getIdToken();
    const client = axios.create({
      baseURL: `${functionsUrl}`,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    //get signed url from firebase function
    const apiResponse = await client.post(
      `brand/${brandId}/stores/r2-signed-url`,
      {
        storeId: storeId,
        fileName: file.name,
      }
    );

    const signedUrlAndFileUrl: { signedUrl: string; fileUrl: string } =
      apiResponse?.data;
    if (!signedUrlAndFileUrl)
      throw new Error("Error getting signed url during upload process.");

    //upload image to r2
    await axios.put(signedUrlAndFileUrl.signedUrl, file, {
      headers: {
        "Content-Type": file.type,
      },
    });

    return signedUrlAndFileUrl.fileUrl;
  } catch (err) {
    throw err;
  }
}

const StoreService = {
  generateId,
  getOne,
  saveOne,
  deleteOne,
  getAllByBrand,
  getAllByIds,
  uploadStoreFile,
};

export default StoreService;
